/*
Loading-skeleton helpers — grey rounded boxes with a shimmer animation,
used while async data is in flight. Replaces the plain "Chargement…"
text on the Arboretum / Species / Badges / Profile / Remarquable
detail screens.

Why a thin component-as-strings module:
- The screens build their initial DOM with `innerHTML = …`, so a
  string-returning helper drops in cleanly.
- We only need three primitives (block, text-line, circle) — the
  full screen skeletons are composed in each screen's render shell
  from these.
- The shimmer animation is a single CSS rule on `.skeleton`; this
  module just emits the markup.
*/

interface SkeletonBlockOptions
{
	width?: string   // CSS length, default '100%'
	height?: string  // CSS length, default '14px' (text-line shape)
	radius?: string  // CSS length, default '8px'
	className?: string  // extra classes appended after `skeleton`
}

/*
Render a single grey rounded box. All dimensions are CSS values —
the caller decides whether to use px / rem / % / clamp. Use this
for thumbnail slots, image placeholders, button shells.
*/
export function renderSkeletonBlock(inOpts: SkeletonBlockOptions = {}): string
{
	const vWidth = inOpts.width ?? '100%'
	const vHeight = inOpts.height ?? '14px'
	const vRadius = inOpts.radius ?? '8px'
	const vClasses = inOpts.className ? `skeleton ${inOpts.className}` : 'skeleton'
	return `<span class="${vClasses}" style="width:${vWidth};height:${vHeight};border-radius:${vRadius};" aria-hidden="true"></span>`
}

/*
Render N stacked text-line skeletons. Lines after the first are
slightly narrower (75 %), mimicking the natural ragged-right look of
real wrapped text so the placeholder doesn't read as a literal grid.
*/
export function renderSkeletonLines(inCount: number, inHeight: string = '12px'): string
{
	const vLines: string[] = []
	for (let vI = 0; vI < inCount; vI++)
	{
		const vWidth = vI === 0 ? '100%' : (vI === inCount - 1 ? '60%' : '85%')
		vLines.push(`<span class="skeleton skeleton-line" style="width:${vWidth};height:${inHeight};" aria-hidden="true"></span>`)
	}
	return vLines.join('')
}

/*
Render a circular skeleton. Used for thumbnail / avatar slots that
end up rendering a circular image.
*/
export function renderSkeletonCircle(inSize: string): string
{
	return `<span class="skeleton" style="width:${inSize};height:${inSize};border-radius:50%;" aria-hidden="true"></span>`
}

/*
Convenience: a skeleton card matching the Arboretum / Remarquables
list-card shape (left glyph + right text stack). Stacks vertically;
caller renders multiple to fill the visible viewport.
*/
export function renderSkeletonListCard(): string
{
	return `
		<div class="skeleton-card" aria-hidden="true">
			${renderSkeletonCircle('44px')}
			<div class="skeleton-card-body">
				${renderSkeletonBlock({ width: '60%', height: '14px' })}
				${renderSkeletonBlock({ width: '40%', height: '11px' })}
			</div>
		</div>
	`
}

/*
Convenience: a skeleton tile for the Badges grid (square-ish glyph +
a label line below). Caller wraps multiple in the badges grid wrap.
*/
export function renderSkeletonBadgeTile(): string
{
	return `
		<div class="skeleton-badge-tile" aria-hidden="true">
			${renderSkeletonCircle('48px')}
			${renderSkeletonBlock({ width: '70%', height: '11px' })}
		</div>
	`
}
